import React, { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import "./VenueShow.css";
import { Grid } from "@mui/material";
import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";
import FavoriteIcon from "@mui/icons-material/Favorite";
import FavoriteBorderIcon from "@mui/icons-material/FavoriteBorder";
import CircularProgress from "@mui/material/CircularProgress";
import { useAuth } from "../../contexts/AuthContext";
import {
  addFavorite,
  getMyFavorites,
  removeFavorite,
} from "../../utils/favoritesApi";
import { AnimatePresence, motion } from "framer-motion";
import { applyDynamicSeo } from "../../utils/dynamicSeo";
import {
  getVenueAvailability,
  getVenueDetail,
  getVenueProducts,
  submitBookingRequest,
  submitLead,
  submitReview,
  trackContactClick,
} from "../../utils/clientAccessApi";

const formatCurrency = (value) => {
  const parsed = Number(value);
  if (!Number.isFinite(parsed)) {
    return null;
  }

  return Math.trunc(parsed).toLocaleString("mk-MK");
};

const formatDate = (value) => {
  if (!value) {
    return "";
  }

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return value;
  }

  return date.toLocaleDateString("mk-MK", {
    year: "numeric",
    month: "long",
    day: "numeric",
  });
};

const formatDateRange = (startDate, endDate) => {
  const start = formatDate(startDate);
  const end = formatDate(endDate);

  if (start && end && start !== end) {
    return `${start} - ${end}`;
  }

  return start || end || "Нема датум";
};

const formatPriceRange = (venue) => {
  const from = formatCurrency(venue?.price_from);
  const to = formatCurrency(venue?.price_to);

  if (from && to) {
    return `${from} - ${to} ден`;
  }

  if (from) {
    return `Од ${from} ден`;
  }

  if (to) {
    return `До ${to} ден`;
  }

  return "Цена по договор";
};

const VIEW_COUNT_DISPLAY_OFFSET = 128;

const normalizeResponseBadge = (value) =>
  String(value || "")
    .trim()
    .toLowerCase()
    .replace(/\s+/g, " ");

const isEmptyResponseBadge = (value) => {
  const normalized = normalizeResponseBadge(value);

  if (!normalized) {
    return true;
  }

  return (
    normalized.includes("no response") ||
    normalized === "без податок" ||
    normalized === "нема податок" ||
    normalized === "n/a"
  );
};

const defaultLeadForm = {
  name: "",
  email: "",
  phone: "",
  wedding_date: "",
  budget: "",
  notes: "",
  product_id: "",
};

const defaultBookingForm = {
  name: "",
  email: "",
  phone: "",
  wedding_date: "",
  budget: "",
  notes: "",
  product_id: "",
};

const defaultReviewForm = {
  reviewer_name: "",
  reviewer_email: "",
  rating: "5",
  body: "",
  event_date: "",
};

const motionItem = {
  hidden: { opacity: 0, y: 18 },
  show: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.3, ease: "easeOut" },
  },
};

const motionSection = {
  hidden: { opacity: 0 },
  show: {
    opacity: 1,
    transition: {
      staggerChildren: 0.08,
      delayChildren: 0.05,
    },
  },
};

function VenueShow() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { authPayload } = useAuth();
  const [venueData, setVenueData] = useState(null);
  const [productsData, setProductsData] = useState({ products: [] });
  const [availability, setAvailability] = useState(null);
  const [availabilityLoading, setAvailabilityLoading] = useState(true);
  const [availabilityError, setAvailabilityError] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [selectedDate, setSelectedDate] = useState("");
  const [isImageOpen, setIsImageOpen] = useState(false);
  const [isFavorite, setIsFavorite] = useState(false);
  const [favoriteBusy, setFavoriteBusy] = useState(false);
  const [favoriteError, setFavoriteError] = useState("");
  const [favoriteNotice, setFavoriteNotice] = useState("");
  const [activeForm, setActiveForm] = useState("inquiry");
  const [leadForm, setLeadForm] = useState(defaultLeadForm);
  const [bookingForm, setBookingForm] = useState(defaultBookingForm);
  const [reviewForm, setReviewForm] = useState(defaultReviewForm);
  const [leadStatus, setLeadStatus] = useState({
    busy: false,
    error: "",
    success: "",
  });
  const [bookingStatus, setBookingStatus] = useState({
    busy: false,
    error: "",
    success: "",
  });
  const [reviewStatus, setReviewStatus] = useState({
    busy: false,
    error: "",
    success: "",
  });

  useEffect(() => {
    let isMounted = true;

    const loadVenueAndProducts = async () => {
      try {
        setLoading(true);
        setError("");

        const [nextVenue, nextProducts] = await Promise.all([
          getVenueDetail(id),
          getVenueProducts(id),
        ]);

        if (!isMounted) {
          return;
        }

        setVenueData(nextVenue);
        setProductsData(nextProducts || { products: [] });
      } catch (fetchError) {
        if (!isMounted) {
          return;
        }

        console.error("Error fetching venue details:", fetchError);
        setError(
          fetchError.message || "Не успеавме да ги вчитаме деталите за понудувачот."
        );
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    loadVenueAndProducts();

    return () => {
      isMounted = false;
    };
  }, [id]);

  useEffect(() => {
    let isMounted = true;

    const loadAvailability = async () => {
      try {
        setAvailabilityLoading(true);
        setAvailabilityError("");

        const params = selectedDate ? { date: selectedDate } : {};
        const nextAvailability = await getVenueAvailability(id, params);

        if (!isMounted) {
          return;
        }

        setAvailability(nextAvailability);
      } catch (fetchError) {
        if (!isMounted) {
          return;
        }

        console.error("Error fetching venue availability:", fetchError);
        setAvailabilityError(
          fetchError.message || "Не успеавме да ја вчитаме достапноста."
        );
      } finally {
        if (isMounted) {
          setAvailabilityLoading(false);
        }
      }
    };

    loadAvailability();

    return () => {
      isMounted = false;
    };
  }, [id, selectedDate]);

  useEffect(() => {
    if (!favoriteNotice) {
      return undefined;
    }

    const timeoutId = window.setTimeout(() => {
      setFavoriteNotice("");
    }, 2200);

    return () => window.clearTimeout(timeoutId);
  }, [favoriteNotice]);

  useEffect(() => {
    const venueRecord = venueData?.venue;
    const venueName = venueRecord?.name;
    if (!venueName) {
      return;
    }

    applyDynamicSeo({
      title: `${venueName} | Kazhavda`,
      description:
        venueRecord.details ||
        "Погледнете детали за свадбена локација, рецензии и контакт опции.",
      imageUrl: venueRecord.image_url,
      type: "business.business",
      keywords: `свадбена локација, ${venueName}, ${venueRecord.city || ""}, казав да`,
    });
  }, [venueData]);

  useEffect(() => {
    const venueId = venueData?.venue?.id || id;

    if (!authPayload?.id_token || !venueId) {
      setIsFavorite(false);
      return undefined;
    }

    let isMounted = true;

    const loadFavoriteState = async () => {
      try {
        setFavoriteError("");
        const payload = await getMyFavorites();
        const venueIdAsString = String(venueId);
        const venueIds = Array.isArray(payload?.venue_ids)
          ? payload.venue_ids.map((value) => String(value))
          : [];
        const items = Array.isArray(payload?.items) ? payload.items : [];
        const fallbackFavorites = Array.isArray(payload)
          ? payload
          : Array.isArray(payload?.favorites)
            ? payload.favorites
            : [];

        const favorited =
          venueIds.includes(venueIdAsString) ||
          items.some(
            (item) =>
              item?.favorite_type === "venue" &&
              String(item?.item_id) === venueIdAsString
          ) ||
          fallbackFavorites.some(
            (favorite) =>
              favorite?.favorite_type === "venue" &&
              String(favorite?.item_id) === venueIdAsString
          );

        if (isMounted) {
          setIsFavorite(favorited);
        }
      } catch (fetchError) {
        console.error("Error fetching favorites:", fetchError);
        if (isMounted) {
          setFavoriteError("Неуспешно вчитување на омилениот статус.");
        }
      }
    };

    loadFavoriteState();

    return () => {
      isMounted = false;
    };
  }, [authPayload?.id_token, id, venueData?.venue?.id]);

  const venueRecord = venueData?.venue;
  const venueImage =
    venueRecord?.image_url || "https://placehold.co/600x400/weddings/white";
  const reviews = Array.isArray(venueData?.reviews) ? venueData.reviews : [];
  const similarVenues = Array.isArray(venueData?.similar_venues)
    ? venueData.similar_venues
    : [];
  const venueProducts = Array.isArray(productsData?.products)
    ? productsData.products
    : [];
  const contactCtas = Array.isArray(venueRecord?.contact_ctas)
    ? venueRecord.contact_ctas
    : [];
  const normalizedResponseBadge = String(
    venueRecord?.response_time_badge || ""
  ).trim();
  const displayResponseBadge =
    normalizedResponseBadge && !isEmptyResponseBadge(normalizedResponseBadge)
      ? normalizedResponseBadge
      : "Во брзо време";
  const displayResponseMeta = venueRecord?.response_time_hours
    ? `${venueRecord.response_time_hours} часа`
    : "Очекувајте одговор во брзо време";
  const displayViews = Math.max(
    Number(venueRecord?.total_views || 0) + VIEW_COUNT_DISPLAY_OFFSET,
    VIEW_COUNT_DISPLAY_OFFSET
  );
  const hasAvailabilityDecision = typeof availability?.likely_available === "boolean";
  const summaryAvailability = selectedDate && hasAvailabilityDecision
    ? availability.likely_available
    : null;
  const availabilityBannerClass = hasAvailabilityDecision
    ? availability.likely_available
      ? "is-available"
      : "is-unavailable"
    : "";
  const availabilityBannerTitle = availabilityLoading && selectedDate
    ? "Се проверува достапноста..."
    : hasAvailabilityDecision
      ? availability.likely_available
        ? "Терминот изгледа слободен"
        : "Можно е терминот да е зафатен"
      : "Изберете датум за да проверите достапност.";
  const availabilityBannerDescription = availabilityError
    ? availabilityError
    : availabilityLoading && selectedDate
      ? "Ги освежуваме блокираните периоди за избраниот датум."
      : availability?.date
        ? `Проверка за ${formatDate(availability.date)}.`
        : "Изберете датум за да проверите достапност.";

  const handleFavoriteToggle = async () => {
    const venueId = venueRecord?.id || id;

    if (!authPayload?.id_token || !venueId || favoriteBusy) {
      return;
    }

    const nextFavoriteState = !isFavorite;
    setFavoriteBusy(true);
    setFavoriteError("");
    setIsFavorite(nextFavoriteState);

    try {
      if (nextFavoriteState) {
        await addFavorite({
          favoriteType: "venue",
          itemId: venueId,
        });
      } else {
        await removeFavorite({
          favoriteType: "venue",
          itemId: venueId,
        });
      }

      setFavoriteNotice(
        nextFavoriteState ? "Додадено во омилени." : "Отстрането од омилени."
      );
    } catch (toggleError) {
      console.error("Error updating favorite:", toggleError);
      setIsFavorite(!nextFavoriteState);
      setFavoriteError("Неуспешно ажурирање на омилени.");
    } finally {
      setFavoriteBusy(false);
    }
  };

  const handleTrackContact = (cta) => {
    if (!venueRecord || !cta?.channel || !cta?.href) {
      return;
    }

    trackContactClick(
      {
        venue_record_id: venueRecord.id,
        channel: cta.channel,
        source_context: "venue_detail",
        target_url: cta.href,
      },
      venueRecord.contact_tracking_endpoint
    );
  };

  const scrollToPlanningSection = () => {
    const section = document.querySelector(".venue-planning-section");
    if (!section) {
      return;
    }

    section.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const handleLeadChange = (key, value) => {
    setLeadForm((current) => ({ ...current, [key]: value }));
  };

  const handleBookingChange = (key, value) => {
    setBookingForm((current) => ({ ...current, [key]: value }));
  };

  const handleReviewChange = (key, value) => {
    setReviewForm((current) => ({ ...current, [key]: value }));
  };

  const handleLeadSubmit = async (event) => {
    event.preventDefault();
    if (!venueRecord) {
      return;
    }

    setLeadStatus({ busy: true, error: "", success: "" });

    try {
      await submitLead(
        {
          ...leadForm,
          budget: leadForm.budget ? Number(leadForm.budget) : null,
          venue_record_id: venueRecord.id,
          product_id: leadForm.product_id || null,
          source: leadForm.product_id ? "product" : "venue",
        },
        venueRecord.inquiry_endpoint
      );

      setLeadStatus({
        busy: false,
        error: "",
        success: "Вашето барање е испратено. Очекувајте одговор од продавачот.",
      });
      setLeadForm({
        ...defaultLeadForm,
        wedding_date: selectedDate || "",
      });
    } catch (submitError) {
      setLeadStatus({
        busy: false,
        error: submitError.message || "Неуспешно испраќање на барањето.",
        success: "",
      });
    }
  };

  const handleBookingSubmit = async (event) => {
    event.preventDefault();
    if (!venueRecord) {
      return;
    }

    setBookingStatus({ busy: true, error: "", success: "" });

    try {
      await submitBookingRequest(venueRecord.id, {
        ...bookingForm,
        budget: bookingForm.budget ? Number(bookingForm.budget) : null,
        product_id: bookingForm.product_id || null,
        source: "venue_detail",
      });

      setBookingStatus({
        busy: false,
        error: "",
        success: "Барањето за термин е испратено успешно.",
      });
      setBookingForm({
        ...defaultBookingForm,
        wedding_date: selectedDate || "",
      });
    } catch (submitError) {
      setBookingStatus({
        busy: false,
        error: submitError.message || "Неуспешно испраќање на барањето.",
        success: "",
      });
    }
  };

  const handleReviewSubmit = async (event) => {
    event.preventDefault();
    if (!venueRecord) {
      return;
    }

    setReviewStatus({ busy: true, error: "", success: "" });

    try {
      await submitReview(venueRecord.id, reviewForm, venueRecord.review_endpoint);
      setReviewStatus({
        busy: false,
        error: "",
        success:
          "Ви благодариме. Вашата рецензија е испратена и чека модерација.",
      });
      setReviewForm(defaultReviewForm);
    } catch (submitError) {
      setReviewStatus({
        busy: false,
        error: submitError.message || "Неуспешно испраќање на рецензијата.",
        success: "",
      });
    }
  };

  if (loading) {
    return (
      <div className="venue-container product-show venue-show">
        <div className="venue-header">
          <div className="venue-header-top">
            <button className="back-button" onClick={() => navigate(-1)}>
              <ArrowBackIosNewIcon fontSize="small" />
            </button>
          </div>
          <div className="venue-hero">
            <img
              src={"https://placehold.co/600x400/lightgray/lightgray"}
              alt="Кажав Да"
              className="venue-image"
            />
          </div>
          <div className="venue-summary">
            <span className="venue-eyebrow blurred"></span>
            <h1 className="venue-title blurred">Кажав Да</h1>
            <div className="venue-meta">
              <span className="blurred"></span>
              <span className="blurred"></span>
            </div>
            <p className="venue-description blurred"></p>
          </div>
        </div>
      </div>
    );
  }

  if (error || !venueRecord) {
    return (
      <div className="venue-container product-show venue-show">
        <div className="venue-error-state">
          <p>{error || "Не успеавме да ги вчитаме деталите."}</p>
          <button className="all-products-button" onClick={() => navigate("/venues")}>
            Назад кон понудувачи
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="venue-container product-show venue-show">
      <motion.div
        className="venue-header"
        initial="hidden"
        animate="show"
        variants={motionSection}
      >
        <div className="venue-header-top">
          <button className="back-button" onClick={() => navigate(-1)}>
            <ArrowBackIosNewIcon fontSize="small" />
          </button>
        </div>
        <motion.div className="venue-hero" variants={motionItem}>
          <img
            src={venueImage}
            alt={venueRecord.name}
            className="venue-image"
            onClick={() => setIsImageOpen(true)}
            role="button"
            tabIndex={0}
            onKeyDown={(event) => {
              if (event.key === "Enter") {
                setIsImageOpen(true);
              }
            }}
          />
        </motion.div>
        <motion.div className="venue-summary" variants={motionItem}>
          <div className="venue-summary-top">
            <span className="venue-eyebrow">VENDOR PROFILE</span>
            {authPayload?.id_token ? (
              <button
                type="button"
                className={`product-favorite-button ${
                  isFavorite ? "is-active" : ""
                }`}
                onClick={handleFavoriteToggle}
                disabled={favoriteBusy}
                aria-label={
                  isFavorite ? "Отстрани од омилени" : "Додај во омилени"
                }
              >
                {favoriteBusy ? (
                  <CircularProgress size={18} />
                ) : isFavorite ? (
                  <FavoriteIcon fontSize="small" />
                ) : (
                  <FavoriteBorderIcon fontSize="small" />
                )}
              </button>
            ) : null}
          </div>
          <h1 className="venue-title">{venueRecord.name}</h1>
          <div className="venue-meta">
            <span className="venue-location-link">
              {venueRecord.city || "Локација по договор"}
            </span>
            <div className="venue-meta-right">
              <span className="venue-price">{formatPriceRange(venueRecord)}</span>
              {summaryAvailability !== null ? (
                <span
                  className={`venue-availability ${
                    summaryAvailability ? "is-available" : "is-unavailable"
                  }`}
                >
                  {summaryAvailability
                    ? "Веројатно достапно"
                    : "Веројатно зафатено"}
                </span>
              ) : null}
            </div>
          </div>
          <p className="venue-description">{venueRecord.details}</p>

          <div className="venue-highlights-grid">
            <div className="venue-highlight-card">
              <span>Оцена</span>
              <strong>
                {venueRecord.rating_average
                  ? `${venueRecord.rating_average}/5`
                  : "Нема оцени"}
              </strong>
              <small>{venueRecord.reviews_count || 0} рецензии</small>
            </div>
            <div className="venue-highlight-card">
              <span>Одговор</span>
              <strong>{displayResponseBadge}</strong>
              <small>{displayResponseMeta}</small>
            </div>
            <div className="venue-highlight-card">
              <span>Прегледи</span>
              <strong>{displayViews}</strong>
              <small>интересирани клиенти</small>
            </div>
            <div className="venue-highlight-card">
              <span>Опсег</span>
              <strong>
                {venueRecord.capacity_min || venueRecord.capacity_max
                  ? `${venueRecord.capacity_min || "?"}-${venueRecord.capacity_max || "?"}`
                  : "По договор"}
              </strong>
              <small>гости</small>
            </div>
          </div>

          {favoriteError ? (
            <p className="favorite-feedback is-error">{favoriteError}</p>
          ) : null}
          {favoriteNotice ? (
            <p className="favorite-feedback">{favoriteNotice}</p>
          ) : null}
        </motion.div>
      </motion.div>

      <AnimatePresence>
      {isImageOpen ? (
        <motion.div
          className="image-modal-backdrop"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={() => setIsImageOpen(false)}
        >
          <motion.div
            className="image-modal-content"
            initial={{ opacity: 0, y: 18, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 12, scale: 0.98 }}
            transition={{ duration: 0.22, ease: "easeOut" }}
            onClick={(event) => event.stopPropagation()}
          >
            <button
              className="image-modal-close"
              onClick={() => setIsImageOpen(false)}
              aria-label="Close"
            >
              ✕
            </button>
            <img
              src={venueImage}
              alt={venueRecord.name}
              className="image-modal-image"
            />
          </motion.div>
        </motion.div>
      ) : null}
      </AnimatePresence>

      <motion.section
        className="venue-cta-section"
        initial="hidden"
        animate="show"
        variants={motionSection}
      >
        <motion.div className="venue-cta-card" variants={motionItem}>
          <div className="venue-section-heading">
            <span className="venue-section-eyebrow">КОНТАКТ</span>
            <h2>Контактирајте го понудувачот</h2>
          </div>
          <div className="venue-cta-grid">
            {contactCtas.map((cta) =>
              cta.channel === "inquiry" ? (
                <button
                  key={`${cta.channel}-${cta.href}`}
                  type="button"
                  className="venue-cta-button is-primary"
                  onClick={() => {
                    setActiveForm("inquiry");
                    handleTrackContact(cta);
                    scrollToPlanningSection();
                  }}
                >
                  {cta.label}
                </button>
              ) : (
                <a
                  key={`${cta.channel}-${cta.href}`}
                  href={cta.href}
                  className="venue-cta-button"
                  onClick={() => handleTrackContact(cta)}
                  target={cta.channel === "maps" ? "_blank" : undefined}
                  rel={cta.channel === "maps" ? "noreferrer" : undefined}
                >
                  {cta.label}
                </a>
              )
            )}
          </div>

          <div className="venue-attribute-list">
            {Array.isArray(venueRecord.service_types) &&
            venueRecord.service_types.length > 0 ? (
              <div className="venue-attribute-group">
                <span>Услуги</span>
                <p>{venueRecord.service_types.join(", ")}</p>
              </div>
            ) : null}
            {Array.isArray(venueRecord.styles) && venueRecord.styles.length > 0 ? (
              <div className="venue-attribute-group">
                <span>Стилови</span>
                <p>{venueRecord.styles.join(", ")}</p>
              </div>
            ) : null}
            {Array.isArray(venueRecord.languages) &&
            venueRecord.languages.length > 0 ? (
              <div className="venue-attribute-group">
                <span>Јазици</span>
                <p>{venueRecord.languages.join(", ")}</p>
              </div>
            ) : null}
          </div>
        </motion.div>

        <motion.div className="venue-cta-card" variants={motionItem}>
          <div className="venue-section-heading">
            <span className="venue-section-eyebrow">ДОСТАПНОСТ</span>
            <h2>Проверете датум</h2>
          </div>

          <label className="venue-form-field">
            <span>Датум на свадба</span>
            <input
              type="date"
              value={selectedDate}
              onChange={(event) => {
                const nextDate = event.target.value;
                setSelectedDate(nextDate);
                setLeadForm((current) => ({ ...current, wedding_date: nextDate }));
                setBookingForm((current) => ({
                  ...current,
                  wedding_date: nextDate,
                }));
              }}
            />
          </label>

          <div
            className={`venue-availability-banner ${availabilityBannerClass}`}
          >
            <strong>{availabilityBannerTitle}</strong>
            <p>{availabilityBannerDescription}</p>
          </div>

          <div className="venue-blocked-ranges">
            <h3>Следни блокирани периоди</h3>
            {availabilityLoading ? (
              <p className="venue-muted-copy">Се вчитува достапноста...</p>
            ) : availabilityError ? (
              <p className="venue-form-feedback is-error">{availabilityError}</p>
            ) : Array.isArray(availability?.blocked_ranges) &&
            availability.blocked_ranges.length > 0 ? (
              availability.blocked_ranges.map((range) => (
                <div className="venue-blocked-range" key={range.id}>
                  <strong>
                    {formatDateRange(range.start_date, range.end_date)}
                  </strong>
                  <span>{range.note || "Без белешка"}</span>
                </div>
              ))
            ) : (
              <p className="venue-muted-copy">Нема најавени блокирани периоди.</p>
            )}
          </div>
        </motion.div>
      </motion.section>

      <motion.section
        className="venue-planning-section"
        initial="hidden"
        animate="show"
        variants={motionSection}
      >
        <div className="venue-section-heading">
          <span className="venue-section-eyebrow">СЛЕДЕН ЧЕКОР</span>
          <h2>Испратете порака, барање за термин или рецензија</h2>
        </div>

        <div className="venue-form-tabs">
          <button
            type="button"
            className={activeForm === "inquiry" ? "is-active" : ""}
            onClick={() => setActiveForm("inquiry")}
          >
            Прашање
          </button>
          <button
            type="button"
            className={activeForm === "booking" ? "is-active" : ""}
            onClick={() => setActiveForm("booking")}
          >
            Термин
          </button>
          <button
            type="button"
            className={activeForm === "review" ? "is-active" : ""}
            onClick={() => setActiveForm("review")}
          >
            Рецензија
          </button>
        </div>

        <AnimatePresence mode="wait">
        {activeForm === "inquiry" ? (
          <motion.form
            key="inquiry"
            className="venue-form-card"
            onSubmit={handleLeadSubmit}
            initial={{ opacity: 0, y: 14 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.22, ease: "easeOut" }}
          >
            <div className="venue-form-grid">
              <label className="venue-form-field">
                <span>Име и презиме</span>
                <input
                  type="text"
                  value={leadForm.name}
                  onChange={(event) => handleLeadChange("name", event.target.value)}
                  required
                />
              </label>
              <label className="venue-form-field">
                <span>Email</span>
                <input
                  type="email"
                  value={leadForm.email}
                  onChange={(event) => handleLeadChange("email", event.target.value)}
                  required
                />
              </label>
              <label className="venue-form-field">
                <span>Телефон</span>
                <input
                  type="tel"
                  value={leadForm.phone}
                  onChange={(event) => handleLeadChange("phone", event.target.value)}
                />
              </label>
              <label className="venue-form-field">
                <span>Датум на свадба</span>
                <input
                  type="date"
                  value={leadForm.wedding_date}
                  onChange={(event) =>
                    handleLeadChange("wedding_date", event.target.value)
                  }
                />
              </label>
              <label className="venue-form-field">
                <span>Буџет</span>
                <input
                  type="number"
                  min="0"
                  value={leadForm.budget}
                  onChange={(event) => handleLeadChange("budget", event.target.value)}
                />
              </label>
              <label className="venue-form-field">
                <span>Поврзан продукт</span>
                <select
                  value={leadForm.product_id}
                  onChange={(event) =>
                    handleLeadChange("product_id", event.target.value)
                  }
                >
                  <option value="">Без конкретен продукт</option>
                  {venueProducts.map((product) => (
                    <option key={product.id} value={product.id}>
                      {product.name}
                    </option>
                  ))}
                </select>
              </label>
              <label className="venue-form-field is-full">
                <span>Порака</span>
                <textarea
                  rows="4"
                  value={leadForm.notes}
                  onChange={(event) => handleLeadChange("notes", event.target.value)}
                  placeholder="Кажете што барате, колку гости очекувате и што ви е важно."
                />
              </label>
            </div>

            {leadStatus.error ? (
              <p className="venue-form-feedback is-error">{leadStatus.error}</p>
            ) : null}
            {leadStatus.success ? (
              <p className="venue-form-feedback is-success">{leadStatus.success}</p>
            ) : null}

            <button
              type="submit"
              className="venue-submit-button"
              disabled={leadStatus.busy}
            >
              {leadStatus.busy ? "Испраќање..." : "Испрати прашање"}
            </button>
          </motion.form>
        ) : null}

        {activeForm === "booking" ? (
          <motion.form
            key="booking"
            className="venue-form-card"
            onSubmit={handleBookingSubmit}
            initial={{ opacity: 0, y: 14 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.22, ease: "easeOut" }}
          >
            <div className="venue-form-grid">
              <label className="venue-form-field">
                <span>Име и презиме</span>
                <input
                  type="text"
                  value={bookingForm.name}
                  onChange={(event) =>
                    handleBookingChange("name", event.target.value)
                  }
                  required
                />
              </label>
              <label className="venue-form-field">
                <span>Email</span>
                <input
                  type="email"
                  value={bookingForm.email}
                  onChange={(event) =>
                    handleBookingChange("email", event.target.value)
                  }
                  required
                />
              </label>
              <label className="venue-form-field">
                <span>Телефон</span>
                <input
                  type="tel"
                  value={bookingForm.phone}
                  onChange={(event) =>
                    handleBookingChange("phone", event.target.value)
                  }
                />
              </label>
              <label className="venue-form-field">
                <span>Датум на свадба</span>
                <input
                  type="date"
                  value={bookingForm.wedding_date}
                  onChange={(event) =>
                    handleBookingChange("wedding_date", event.target.value)
                  }
                  required
                />
              </label>
              <label className="venue-form-field">
                <span>Буџет</span>
                <input
                  type="number"
                  min="0"
                  value={bookingForm.budget}
                  onChange={(event) =>
                    handleBookingChange("budget", event.target.value)
                  }
                />
              </label>
              <label className="venue-form-field">
                <span>Продукт</span>
                <select
                  value={bookingForm.product_id}
                  onChange={(event) =>
                    handleBookingChange("product_id", event.target.value)
                  }
                >
                  <option value="">Општо барање за терминот</option>
                  {venueProducts.map((product) => (
                    <option key={product.id} value={product.id}>
                      {product.name}
                    </option>
                  ))}
                </select>
              </label>
              <label className="venue-form-field is-full">
                <span>Белешка</span>
                <textarea
                  rows="4"
                  value={bookingForm.notes}
                  onChange={(event) =>
                    handleBookingChange("notes", event.target.value)
                  }
                  placeholder="Напишете што сакате да резервирате и какви се вашите очекувања."
                />
              </label>
            </div>

            {bookingStatus.error ? (
              <p className="venue-form-feedback is-error">
                {bookingStatus.error}
              </p>
            ) : null}
            {bookingStatus.success ? (
              <p className="venue-form-feedback is-success">
                {bookingStatus.success}
              </p>
            ) : null}

            <button
              type="submit"
              className="venue-submit-button"
              disabled={bookingStatus.busy}
            >
              {bookingStatus.busy ? "Испраќање..." : "Побарај термин"}
            </button>
          </motion.form>
        ) : null}

        {activeForm === "review" ? (
          <motion.form
            key="review"
            className="venue-form-card"
            onSubmit={handleReviewSubmit}
            initial={{ opacity: 0, y: 14 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.22, ease: "easeOut" }}
          >
            <div className="venue-form-grid">
              <label className="venue-form-field">
                <span>Вашето име</span>
                <input
                  type="text"
                  value={reviewForm.reviewer_name}
                  onChange={(event) =>
                    handleReviewChange("reviewer_name", event.target.value)
                  }
                  required
                />
              </label>
              <label className="venue-form-field">
                <span>Email</span>
                <input
                  type="email"
                  value={reviewForm.reviewer_email}
                  onChange={(event) =>
                    handleReviewChange("reviewer_email", event.target.value)
                  }
                  required
                />
              </label>
              <label className="venue-form-field">
                <span>Оцена</span>
                <select
                  value={reviewForm.rating}
                  onChange={(event) =>
                    handleReviewChange("rating", event.target.value)
                  }
                >
                  <option value="5">5</option>
                  <option value="4">4</option>
                  <option value="3">3</option>
                  <option value="2">2</option>
                  <option value="1">1</option>
                </select>
              </label>
              <label className="venue-form-field">
                <span>Датум на настан</span>
                <input
                  type="date"
                  value={reviewForm.event_date}
                  onChange={(event) =>
                    handleReviewChange("event_date", event.target.value)
                  }
                />
              </label>
              <label className="venue-form-field is-full">
                <span>Ваше искуство</span>
                <textarea
                  rows="5"
                  value={reviewForm.body}
                  onChange={(event) => handleReviewChange("body", event.target.value)}
                  required
                  placeholder="Што ви се допадна, како помина комуникацијата и дали би го препорачале?"
                />
              </label>
            </div>

            {reviewStatus.error ? (
              <p className="venue-form-feedback is-error">{reviewStatus.error}</p>
            ) : null}
            {reviewStatus.success ? (
              <p className="venue-form-feedback is-success">
                {reviewStatus.success}
              </p>
            ) : null}

            <button
              type="submit"
              className="venue-submit-button"
              disabled={reviewStatus.busy}
            >
              {reviewStatus.busy ? "Испраќање..." : "Испрати рецензија"}
            </button>
          </motion.form>
        ) : null}
        </AnimatePresence>
      </motion.section>

      <motion.section
        className="venue-reviews-section"
        initial="hidden"
        animate="show"
        variants={motionSection}
      >
        <div className="venue-section-heading">
          <span className="venue-section-eyebrow">РЕЦЕНЗИИ</span>
          <h2>Што велат клиентите</h2>
        </div>

        {reviews.length > 0 ? (
          <div className="venue-reviews-grid">
            {reviews.map((review) => (
              <motion.article
                className="venue-review-card"
                key={review.id}
                variants={motionItem}
              >
                <div className="venue-review-topline">
                  <strong>{review.reviewer_name}</strong>
                  <span>{review.rating}/5</span>
                </div>
                <p>{review.body}</p>
                <div className="venue-review-meta">
                  <span>{formatDate(review.event_date || review.created_at)}</span>
                  {review.vendor_response ? (
                    <small>
                      Одговор од продавачот: {review.vendor_response}
                    </small>
                  ) : null}
                </div>
              </motion.article>
            ))}
          </div>
        ) : (
          <p className="venue-muted-copy">
            Сè уште нема објавени рецензии. Бидете први што ќе споделат искуство.
          </p>
        )}
      </motion.section>

      {similarVenues.length > 0 ? (
        <motion.section
          className="products-section"
          initial="hidden"
          animate="show"
          variants={motionSection}
        >
          <h2 className="products-section-title">Слични понудувачи</h2>
          <Grid
            container
            className="products-list"
            spacing={2}
            direction={{ xs: "column", sm: "row" }}
            justifyContent="flex-start"
            alignItems={{ xs: "center", sm: "flex-start" }}
            wrap="wrap"
            sx={{
              px: { xs: 2, sm: 4 },
            }}
          >
            {similarVenues.map((similarVenue) => (
              <Grid item size={{ xs: 12, sm: 6, lg: 4 }} key={similarVenue.id}>
                <Link to={`/venue/${similarVenue.id}`} className="product-card-link">
                    <motion.div
                      className="product-card similar-venue-card"
                      variants={motionItem}
                      whileHover={{ y: -6 }}
                      transition={{ duration: 0.22, ease: "easeOut" }}
                    >
                    <img
                      src={
                        similarVenue.image_url ||
                        "https://placehold.co/300x200/weddings/white"
                      }
                      alt={similarVenue.name}
                      className="product-image"
                    />
                    <div className="product-card-content">
                      <h3 className="product-title">{similarVenue.name}</h3>
                      <p className="product-price">{similarVenue.city || "Локација по договор"}</p>
                      <p className="similar-venue-meta">
                        {similarVenue.rating_average
                          ? `${similarVenue.rating_average}/5`
                          : "Нема оцени"}{" "}
                        · {similarVenue.reviews_count || 0} рецензии
                      </p>
                    </div>
                    </motion.div>
                  </Link>
                </Grid>
              ))}
          </Grid>
        </motion.section>
      ) : null}

      <motion.div
        className="products-section"
        initial="hidden"
        animate="show"
        variants={motionSection}
      >
        <h2 className="products-section-title">Продукти</h2>
        <Grid
          container
          className="products-list"
          spacing={2}
          direction={{ xs: "column", sm: "row" }}
          justifyContent="flex-start"
          alignItems={{ xs: "center", sm: "flex-start" }}
          wrap="wrap"
          sx={{
            px: { xs: 2, sm: 4 },
          }}
        >
          {venueProducts.length > 0 ? (
            venueProducts.map((product) => (
              <Grid item size={{ xs: 12, sm: 6, lg: 4 }} key={product.id}>
                <Link
                  to={`/product/${product.id}?viewed_from=standard`}
                  className="product-card-link"
                >
                  <motion.div
                    className="product-card"
                    variants={motionItem}
                    whileHover={{ y: -6 }}
                    transition={{ duration: 0.22, ease: "easeOut" }}
                  >
                    <img
                      src={
                        product.images?.[0] ||
                        "https://placehold.co/300x200/weddings/white"
                      }
                      alt={product.name}
                      className="product-image"
                    />
                    <div className="product-card-content">
                      <h3 className="product-title">{product.name}</h3>
                      <p className="product-price">
                        {formatCurrency(product.price) || "Цена по договор"}
                        {formatCurrency(product.price) ? <small> ден</small> : null}
                      </p>
                      {selectedDate && typeof product.likely_available === "boolean" ? (
                        <p className="product-availability-note">
                          {product.likely_available
                            ? "Веројатно достапно за избраниот датум"
                            : "Можно е да е зафатено за избраниот датум"}
                        </p>
                      ) : null}
                    </div>
                  </motion.div>
                </Link>
              </Grid>
            ))
          ) : (
            <div className="no-products">
              Нема додадено производи во моментов...
            </div>
          )}
        </Grid>
        <div className="products-action">
          <Link to="/all-products" className="all-products-button">
            Сите производи
          </Link>
        </div>
      </motion.div>
    </div>
  );
}

export default VenueShow;
